/**
 * Sell-tab alerts — persistent AudioContext, auto-warm on any interaction,
 * Notification + voice fallback when Web Audio is blocked.
 */

let audioCtx = null;
let audioWarmed = false;
let titleFlashTimer = null;
let originalTitle = '';
const pendingAlerts = [];

function getAudioContext() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx || audioCtx.state === 'closed') audioCtx = new Ctx();
  return audioCtx;
}

function runBuzzOnContext(ctx) {
  const master = ctx.createGain();
  master.gain.value = 1.0;
  master.connect(ctx.destination);
  const t0 = ctx.currentTime;
  for (let i = 0; i < 18; i += 1) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = i % 2 === 0 ? 920 : 680;
    g.gain.setValueAtTime(0.95, t0 + i * 0.14);
    g.gain.exponentialRampToValueAtTime(0.01, t0 + i * 0.14 + 0.11);
    osc.connect(g);
    g.connect(master);
    osc.start(t0 + i * 0.14);
    osc.stop(t0 + i * 0.14 + 0.12);
  }
}

/** Unlock Web Audio for this tab session (silent buffer trick). */
export async function warmSellAudio() {
  const ctx = getAudioContext();
  if (!ctx) return false;
  try {
    if (ctx.state === 'suspended') await ctx.resume();
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
    audioWarmed = ctx.state === 'running';
    return audioWarmed;
  } catch {
    return false;
  }
}

function flushPendingAlerts() {
  if (!pendingAlerts.length || !audioWarmed) return;
  pendingAlerts.splice(0, pendingAlerts.length);
  tryPlayBuzz();
}

function tryPlayBuzz() {
  const ctx = getAudioContext();
  if (!ctx) return false;
  try {
    if (ctx.state === 'suspended') {
      ctx.resume().then(() => {
        if (ctx.state === 'running') {
          audioWarmed = true;
          runBuzzOnContext(ctx);
        }
      }).catch(() => {});
      return false;
    }
    runBuzzOnContext(ctx);
    return true;
  } catch {
    return false;
  }
}

export function playSellVoice(stock, move) {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const name = String(stock || 'stock').replace(/\.(NS|BO)$/i, '');
    const moveTxt = move != null && Number.isFinite(Number(move))
      ? `Plus ${Number(move).toFixed(2)} rupees per share`
      : 'Profit target hit';
    const text = `Sell now! ${name}. ${moveTxt}. Sell on Groww.`;
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'en-IN';
    utter.rate = 0.92;
    utter.pitch = 1.08;
    utter.volume = 1;
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find((v) => /en-IN|en-GB|en-US/i.test(v.lang));
    if (preferred) utter.voice = preferred;
    window.speechSynthesis.speak(utter);
  } catch {
    /* ignore */
  }
}

/** Sheet: BUY timeout → HOLD — voice "moving to hold tab". */
export function playMovingToHoldVoice(stock) {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const name = String(stock || 'stock').replace(/\.(NS|BO)$/i, '');
    const text = `Moving to hold tab. ${name}.`;
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'en-IN';
    utter.rate = 0.95;
    utter.pitch = 1;
    utter.volume = 1;
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find((v) => /en-IN|en-GB|en-US/i.test(v.lang));
    if (preferred) utter.voice = preferred;
    window.speechSynthesis.speak(utter);
  } catch {
    /* ignore */
  }
}

/** Sheet stop-loss → Sold Today (skips SELL because SELL needs profit). */
export function playStopLossSoldVoice(stock, totalLossRs) {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const name = String(stock || 'stock').replace(/\.(NS|BO)$/i, '');
    const lossTxt = totalLossRs != null && Number.isFinite(Number(totalLossRs))
      ? `${Number(totalLossRs).toFixed(2)} rupees total`
      : 'stop loss';
    const text = `Stop loss. ${name}. ${lossTxt}. Sold today.`;
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'en-IN';
    utter.rate = 0.95;
    utter.pitch = 0.95;
    utter.volume = 1;
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find((v) => /en-IN|en-GB|en-US/i.test(v.lang));
    if (preferred) utter.voice = preferred;
    window.speechSynthesis.speak(utter);
  } catch {
    /* ignore */
  }
}

function fireSellNotification(stock, move) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    const moveTxt = move != null ? `+₹${Number(move).toFixed(2)}/sh` : '+₹0.50/sh';
    new Notification('SELL NOW — Normal Trading V1', {
      body: `${stock || 'Stock'} hit ${moveTxt}. Sell on Groww now.`,
      tag: `v1-sell-${stock || 'alert'}`,
      requireInteraction: true
    });
  } catch {
    /* ignore */
  }
}

function flashSellTitle(stock, customLabel) {
  if (typeof document === 'undefined') return;
  if (!originalTitle) originalTitle = document.title;
  if (titleFlashTimer) return;
  let on = true;
  const label = customLabel || (stock ? `🔴 SELL ${stock}` : '🔴 SELL NOW');
  titleFlashTimer = setInterval(() => {
    document.title = on ? label : originalTitle;
    on = !on;
  }, 700);
  setTimeout(() => {
    if (titleFlashTimer) {
      clearInterval(titleFlashTimer);
      titleFlashTimer = null;
      document.title = originalTitle;
    }
  }, 20000);
}

/** Request desktop notifications once (OS may play its own alert sound). */
export function requestSellNotifications() {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'default') {
    Notification.requestPermission().catch(() => {});
  }
}

/** Attach warm listeners — any scroll/click/key unlocks audio for the session. */
export function armSellAudioAutoUnlock() {
  const warm = () => {
    warmSellAudio().then((ok) => {
      if (ok) flushPendingAlerts();
    });
  };
  const events = ['pointerdown', 'touchstart', 'keydown', 'click', 'scroll', 'wheel', 'mousemove'];
  events.forEach((ev) => document.addEventListener(ev, warm, { passive: true, capture: true }));
  warm();
  return () => events.forEach((ev) => document.removeEventListener(ev, warm, { capture: true }));
}

/**
 * Full sell alert — always fires (no manual unlock gate).
 * Buzz + voice + notification + title flash.
 */
export function fireSellAlert({ stock, move } = {}) {
  warmSellAudio().then((warmed) => {
    if (warmed && tryPlayBuzz()) return;
    pendingAlerts.push({ stock, move });
  });
  playSellVoice(stock, move);
  fireSellNotification(stock, move);
  flashSellTitle(stock);
}

export function playSellBuzz() {
  const ctx = getAudioContext();
  if (!ctx) return;
  warmSellAudio().then(() => {
    if (ctx.state === 'running') runBuzzOnContext(ctx);
  });
}

function runLoudCncProfitBuzz(ctx) {
  const master = ctx.createGain();
  master.gain.value = 1.55;
  master.connect(ctx.destination);
  const t0 = ctx.currentTime;
  for (let i = 0; i < 30; i += 1) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = i % 2 === 0 ? 1150 : 780;
    g.gain.setValueAtTime(1.0, t0 + i * 0.1);
    g.gain.exponentialRampToValueAtTime(0.01, t0 + i * 0.1 + 0.09);
    osc.connect(g);
    g.connect(master);
    osc.start(t0 + i * 0.1);
    osc.stop(t0 + i * 0.1 + 0.1);
  }
}

function tryPlayLoudCncProfitBuzz() {
  const ctx = getAudioContext();
  if (!ctx) return false;
  try {
    if (ctx.state === 'suspended') {
      ctx.resume().then(() => {
        if (ctx.state === 'running') {
          audioWarmed = true;
          runLoudCncProfitBuzz(ctx);
        }
      }).catch(() => {});
      return false;
    }
    runLoudCncProfitBuzz(ctx);
    return true;
  } catch {
    return false;
  }
}

export function playLoudCncProfitBeep() {
  const ctx = getAudioContext();
  if (!ctx) return;
  warmSellAudio().then(() => {
    if (ctx.state === 'running') runLoudCncProfitBuzz(ctx);
  });
}

function playCncProfitVoice(stock, movePct) {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const name = stock || 'stock';
    const pct = movePct != null ? Number(movePct).toFixed(1) : '1';
    const text = `CNC profit hit! Sell ${name} now. Plus ${pct} percent. Sell on Groww.`;
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'en-IN';
    utter.rate = 0.92;
    utter.pitch = 1.1;
    utter.volume = 1;
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find((v) => /en-IN|en-GB|en-US/i.test(v.lang));
    if (preferred) utter.voice = preferred;
    window.speechSynthesis.speak(utter);
  } catch {
    /* ignore */
  }
}

function fireCncProfitNotification(stock, movePct) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    const pctTxt = movePct != null ? `+${Number(movePct).toFixed(2)}%` : '+1%';
    new Notification('CNC PROFIT — SELL NOW', {
      body: `${stock || 'Stock'} hit ${pctTxt} action target. Book profit on Groww.`,
      tag: `cnc-profit-${stock || 'alert'}`,
      requireInteraction: true
    });
  } catch {
    /* ignore */
  }
}

/** Loud buzz + voice + notification when CNC action screen hits +1% profit. */
export function fireCncProfitAlert({ stock, movePct } = {}) {
  warmSellAudio().then((warmed) => {
    if (warmed && tryPlayLoudCncProfitBuzz()) return;
    pendingAlerts.push({ stock, move: movePct });
  });
  playCncProfitVoice(stock, movePct);
  fireCncProfitNotification(stock, movePct);
  flashSellTitle(stock, stock ? `🟢 CNC SELL ${stock}` : '🟢 CNC PROFIT');
}

export function playSoldDoneChime() {
  const ctx = getAudioContext();
  if (!ctx) return;
  warmSellAudio().then(() => {
    try {
      if (ctx.state !== 'running') return;
      const master = ctx.createGain();
      master.gain.value = 0.55;
      master.connect(ctx.destination);
      const t0 = ctx.currentTime;
      [660, 880].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        g.gain.setValueAtTime(0.001, t0 + i * 0.12);
        g.gain.exponentialRampToValueAtTime(0.35, t0 + i * 0.12 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, t0 + i * 0.12 + 0.28);
        osc.connect(g);
        g.connect(master);
        osc.start(t0 + i * 0.12);
        osc.stop(t0 + i * 0.12 + 0.3);
      });
    } catch {
      /* ignore */
    }
  });
}
